import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { useSelector } from 'react-redux';
import { getJobTask } from 'http/jobsTasks';
import { getJobTaskVersions } from 'http/jobTaskVersions';
import { getCompanyJobRoleVersions } from 'http/companyProvisioning';
import { routesAliases } from 'router/routesAliases';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROLES } from 'appConstants';
import JobTaskVersionRequirementsEditor from '../JobTaskVersionRequirementsEditor';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(4, 5),
  },
}));

const JobTaskVersionRequirements = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const userRole = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = userRole === ROLES.HYPER;
  const isSuperAdmin = userRole === ROLES.SUPER;
  const { jobTaskId, versionId } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [jobTask, setJobTask] = useState(null);
  const [version, setVersion] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [isFromSystem, setIsFromSystem] = useState(false);

  const backUrl = `${routesAliases.editJobTask}/${jobTaskId}`;
  const useSystemVersionApis = isHyperAdmin && !isFromSystem;
  const apiMode = useSystemVersionApis ? 'system' : 'company';

  const [fetchState, fetchData] = useAsyncFn(async () => {
    const jobTaskResult = await getJobTask(jobTaskId);

    if (jobTaskResult?.error) {
      if (jobTaskResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { jobTaskResult };
    }

    setJobTask(jobTaskResult);
    const fromSystem = Boolean(jobTaskResult.isFromSystem);
    setIsFromSystem(fromSystem);

    const useSystemApis = isHyperAdmin && !fromSystem;
    const versionsResult = useSystemApis
      ? await getJobTaskVersions({ jobTaskId, page: 0, pageSize: 100 })
      : await getCompanyJobRoleVersions(jobTaskId, { page: 0, pageSize: 100 });

    if (versionsResult?.error || versionsResult?.statusCode) {
      return { jobTaskResult, versionsResult };
    }

    const items = versionsResult.items || [];
    setAllVersions(items);
    const foundVersion = items.find(item => item.id === Number(versionId));
    if (!foundVersion) {
      setNotFoundError(true);
      return { jobTaskResult, versionsResult };
    }

    setVersion(foundVersion);
    setNotFoundError(false);
    return { jobTaskResult, versionsResult };
  }, [jobTaskId, versionId, isHyperAdmin]);

  useEffect(() => {
    setNotFoundError(false);
    setJobTask(null);
    setVersion(null);
    setAllVersions([]);
    setIsFromSystem(false);
    fetchData();
  }, [jobTaskId, versionId, fetchData]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: jobTask?.name || t('routes.editJobTask'),
        link: backUrl,
      },
      {
        title: t('routes.jobTaskVersionRequirements'),
        isActive: true,
      },
    ],
    [backUrl, jobTask?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const handleBack = () =>
    history.push({
      pathname: backUrl,
      state: location.state,
    });
  const isReadOnly = isSuperAdmin && isFromSystem;

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !version}
    >
      {version && (
        <JobTaskVersionRequirementsEditor
          version={version}
          allVersions={allVersions}
          onCancel={handleBack}
          layout="page"
          apiMode={apiMode}
          jobRoleId={Number(jobTaskId)}
          isReadOnly={isReadOnly}
        />
      )}
    </PageLayout>
  );
};

JobTaskVersionRequirements.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default JobTaskVersionRequirements;
